import { useEffect, useMemo, useState } from 'react';
import { hrApi } from '@/api/hr.api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { SelectOption } from '@/components/ui/Select';
import type { HrEmploymentType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';

export function EmploymentTypeSelect({
  label = 'Employment type',
  value = '',
  onChange,
  placeholder = 'Select employment type',
  className,
  required,
  valueMode = 'code',
}: {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  /** Use employment type `code` (default) or numeric `id`. */
  valueMode?: 'code' | 'id';
}) {
  const [types, setTypes] = useState<HrEmploymentType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrApi
      .listEmploymentTypes()
      .then(setTypes)
      .catch((err) => showApiError(err, 'Failed to load employment types'))
      .finally(() => setLoading(false));
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      types
        .filter((t) => t.active)
        .map((type) => ({
          value: valueMode === 'id' ? String(type.id) : type.code,
          label: `${type.name} (${type.code})`,
        })),
    [types, valueMode],
  );

  return (
    <SearchableSelect
      className={className}
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      loading={loading}
      searchPlaceholder="Search employment types…"
      emptyMessage="No employment types — configure them in HR Master data"
    />
  );
}
